import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@components/common/modal/modal';
import {
	getSiteVisitRecordingElapsedMs,
	stopSiteVisitRecording,
} from '@services/siteVisitRecording/siteVisitRecordingEngine';
import { isSiteVisitRecordingActive, useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';
import { SiteVisitRecordingDurationReminderModal } from './SiteVisitRecordingDurationReminderModal';

const VISIT_FORM_REMINDER_MS = 45 * 60 * 1000;
const VISIT_FORM_MAX_DURATION_MS = 60 * 60 * 1000;
const LIMIT_CHECK_INTERVAL_MS = 1000;

/**
 * Visit form / draft form only: 45-minute reminder and 1-hour hard stop.
 * BDE Feedback uses {@link BdeFeedbackDurationLimitHandler} instead.
 */
export function SiteVisitRecordingDurationLimitHandler(): ReactElement | null {
	const phase = useSiteVisitRecordingStore((s) => s.phase);
	const uploadOnStop = useSiteVisitRecordingStore((s) => s.uploadOnStop);
	const [reminderOpen, setReminderOpen] = useState(false);
	const reminderDismissedRef = useRef(false);
	const hardStopTriggeredRef = useRef(false);
	const isVisitFormActive = !uploadOnStop && isSiteVisitRecordingActive(phase);

	useEffect(() => {
		if (!isVisitFormActive) {
			reminderDismissedRef.current = false;
			hardStopTriggeredRef.current = false;
			setReminderOpen(false);
			return;
		}

		const checkDurationLimits = (): void => {
			const elapsedMs = getSiteVisitRecordingElapsedMs();

			if (elapsedMs >= VISIT_FORM_MAX_DURATION_MS && !hardStopTriggeredRef.current) {
				hardStopTriggeredRef.current = true;
				setReminderOpen(false);
				void stopSiteVisitRecording();
				return;
			}

			if (elapsedMs >= VISIT_FORM_REMINDER_MS && !reminderDismissedRef.current && !hardStopTriggeredRef.current) {
				setReminderOpen(true);
			}
		};

		checkDurationLimits();
		const intervalId = window.setInterval(checkDurationLimits, LIMIT_CHECK_INTERVAL_MS);
		return (): void => window.clearInterval(intervalId);
	}, [isVisitFormActive]);

	const handleContinue = useCallback((): void => {
		reminderDismissedRef.current = true;
		setReminderOpen(false);
	}, []);

	const handleStop = useCallback((): void => {
		reminderDismissedRef.current = true;
		setReminderOpen(false);
		void stopSiteVisitRecording();
	}, []);

	if (!reminderOpen) {
		return null;
	}

	return (
		<Modal
			modalOpen={reminderOpen}
			modalId="siteVisitRecordingDurationReminder"
			onModalClose={handleContinue}
			className="dialog-sm site-lead-confrim"
			isEscAllow={false}
		>
			<SiteVisitRecordingDurationReminderModal onStop={handleStop} onContinue={handleContinue} />
		</Modal>
	);
}
